import {ComponentType, ReactElement, ReactNode, useEffect, useState} from 'react'
import {I18nContextValues, Trans, withNamespaces} from 'react-i18next'
import {flowRight as compose, first, get} from 'lodash'
import {graphql} from 'react-apollo'
import withStyles, {StyledComponentProps} from '@material-ui/core/styles/withStyles'
import {Link, RouteComponentProps, withRouter} from 'react-router-dom'
import {Grid} from '@material-ui/core'
import {accountSubtypes, IAccountSubtypeEnum, KycStatusEnumIndices} from '@bdswiss/common-enums'
import {ACCOUNT_HAS_OPEN_POSITIONS_QUERY} from '../../../../../graphql/queries'
import PageSubTitle from '../../../../Common/PageSubTitle'
import messages from '../../../../../assets/messages'
import NotificationBar from '../../../../Common/NotificationBar'
import {UnableCopyTradeAlertDialog} from '../../components/UnableCopyTradeAlertDialog'
import {Client, Account, BrokereeCopyTradingOffer} from '../../types'
import {OfferCard} from './components/OfferCard'
import {OpenPositionsNotification} from './components/OpenPositionsNotification'
import {SubscribeAlertDialog} from './components/SubscribeAlertDialog'
import {UnsubscribeAlertDialog} from './components/UnsubscribeAlertDialog'
import {useToggleSubscription} from './hooks/useToggleSubscription'
import {styles} from './styles'

type Message = {
  i18nKey: string
  defaults: string
}

type RequestedAction = {
  offer: BrokereeCopyTradingOffer
  type: ActionType
}

type BrokereeCopyTradingOffersPropsBase = {
  children?: ReactNode
  client: Client
  account: Account
  offers: BrokereeCopyTradingOffer[]
  currentOfferSubscription: BrokereeCopyTradingOffer | null
  hasActiveLegacySubscription: boolean
  isLoading: boolean
  onUpdated: () => void
  onRefetch: () => void
}

type BrokereeCopyTradingOffersProps = BrokereeCopyTradingOffersPropsBase
  & StyledComponentProps
  & I18nContextValues
  & RouteComponentProps
  & {
    kycStatus: KycStatusEnumIndices
    hasOpenPositions: boolean
    refetchHasOpenPositions: () => void
  }

enum ActionType {
  Subscribe = 'subscribe',
  Unsubscribe = 'unsubscribe',
  Error = 'error',
}

const REFETCH_INTERVAL_MS =  1000 * 10 // 10 seconds

const Component = (props: BrokereeCopyTradingOffersProps): ReactElement => {
  const {
    classes = {},
    t,
    children,
    offers,
    currentOfferSubscription,
    client,
    account,
    hasOpenPositions,
    hasActiveLegacySubscription,
    refetchHasOpenPositions,
    onUpdated,
    onRefetch
  } = props

  const [successMessage, setSuccessMessage] = useState<Message | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const [requestedAction, setRequestedAction] = useState<RequestedAction | null>(null)

  const hasActiveSubscriptions = currentOfferSubscription != null || hasActiveLegacySubscription
  const providerAccountSubtypeKey = requestedAction?.offer.provider.account.subtype
  const providerAccountSubtype = (providerAccountSubtypeKey != null ? accountSubtypes[providerAccountSubtypeKey] : null) as IAccountSubtypeEnum | null

  const handleToggleSuccess = async (isSubscribed: boolean): Promise<void> => {
    await onUpdated()
    await refetchHasOpenPositions()
    setRequestedAction(null)
    setErrorMessage(null)

    const message = isSubscribed ? messages.brokereeSubscribeSuccess : messages.brokereeUnsubscribeSuccess
    setSuccessMessage(message)
  }

  const handleToggleError = (): void => {
    // TODO: update error message
    setErrorMessage(t(messages.somethingWrongError.i18nKey, messages.somethingWrongError.defaults))
    setSuccessMessage(null)
  }

  const {toggleSubscription, loading} = useToggleSubscription(account.id, {
    onSuccess: handleToggleSuccess,
    onError: handleToggleError
  })

  const handleSubscribeRequest = (offer: BrokereeCopyTradingOffer): void => {
    const hasSameAccountSubtype = offer.provider.account.subtype === account.accountSubtype
    const type = hasSameAccountSubtype ? ActionType.Subscribe : ActionType.Error
    setRequestedAction({offer, type})
  }
  const handleUnsubscribeRequest = (offer: BrokereeCopyTradingOffer): void => {
    setRequestedAction({offer, type: ActionType.Unsubscribe})
  }

  const handleNotificationClose = (): void => setSuccessMessage(null)

  const handleSubscribeAgree = (): void => {
    if (requestedAction?.offer == null) return
    toggleSubscription(requestedAction.offer.id, true)
  }

  const handleUnsubscribeAgree = (): void => {
    if (requestedAction?.offer == null) return
    toggleSubscription(requestedAction.offer.id, false)
  }

  const handleDisagree = (): void => setRequestedAction(null)

  useEffect(() => {
    const intervalId = setInterval(() => {
      onRefetch()
    }, REFETCH_INTERVAL_MS)

    return () => clearInterval(intervalId)
  }, [onRefetch])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} >
        <PageSubTitle>
          <Trans {...messages.availableStrategies} />
        </PageSubTitle>
      </Grid>
      <Grid container spacing={2}>
        {(account.isReadOnly && !hasActiveSubscriptions) ? (
          <Grid item xs={12}>
            <NotificationBar status='info'>
              <Trans {...messages.depositPendingCopying} />
              <Link to={`/transactions/${account.id}/deposit`} className={classes.link}>
                <Trans {...messages.depositNow} />
              </Link>
            </NotificationBar>
          </Grid>
        ): null}
        {!account.isReadOnly && hasOpenPositions && !hasActiveSubscriptions ? (
          <OpenPositionsNotification account={account} />
        ) : null}
        {successMessage != null ? (
          <Grid item xs={12}>
            <NotificationBar status="success" onClose={handleNotificationClose}>
              <Trans
                {...successMessage}
                components={[<Link to={'./settings'} className={classes.link}> </Link>]}
              />
            </NotificationBar>
          </Grid>
        ) : null}
        {offers.map(offer => (
          <OfferCard
            key={offer.id}
            client={client}
            account={account}
            offer={offer}
            hasOpenPositions={hasOpenPositions}
            hasActiveSubscription={hasActiveSubscriptions}
            onSubscribeRequest={handleSubscribeRequest}
            onUnsubscribeRequest={handleUnsubscribeRequest}
          />
        ))}
        {children}
      </Grid>
      <SubscribeAlertDialog
        offer={requestedAction?.offer ?? null}
        errorMessage={errorMessage}
        isOpen={requestedAction?.type === ActionType.Subscribe}
        isLoading={loading}
        onAgree={handleSubscribeAgree}
        onDisagree={handleDisagree}
      />
      <UnsubscribeAlertDialog
        offer={requestedAction?.offer ?? null}
        errorMessage={errorMessage}
        isOpen={requestedAction?.type === ActionType.Unsubscribe}
        isLoading={loading}
        onAgree={handleUnsubscribeAgree}
        onDisagree={handleDisagree}
      />
      <UnableCopyTradeAlertDialog
        strategyAccountSubtype={providerAccountSubtype?.label ?? '-'}
        isOpen={requestedAction?.type === ActionType.Error}
        onClose={handleDisagree}
        />
    </Grid>)
}

export const BrokereeCopyTradingOffers: ComponentType<BrokereeCopyTradingOffersPropsBase> = compose(
  withStyles(styles),
  withNamespaces(),
  withRouter,
  // Legacy way of getting data for conditions
  graphql(ACCOUNT_HAS_OPEN_POSITIONS_QUERY, {
    options: (props:any) => ({variables: {accountId: Number(props.match.params.accountId)}}),
    props: ({data}:any) => {
      const hasOpenPositions = get(first(get(data, 'viewer.accounts', [])), 'hasOpenPositions')
      return {hasOpenPositions, refetchHasOpenPositions: data.refetch}
    }
  }),
)(Component)
